import * as Sentry from "@sentry/react";
import type { NaniClubResponse } from "@/types/apis/naniclub";
import { getToken } from "@/utils/auth/helpers";
import { handleUnauthorized } from "@/utils/auth/handleUnauthorized";
import { UnauthorizedError } from "@/utils/auth/UnauthorizedError";
import { sentryMarkAsReported } from "@/utils/sentry.helper";

type FetchNaniClubOptions = {
  url: string;
  body?: unknown;
  method?: string;
};

function getGeneralizedPath(url: string): string {
  return url.replace(/\/\d+/g, "/{id}");
}

export const fetchNaniClub = async <T>(
  options: FetchNaniClubOptions
): Promise<T> => {
  const { url, body, method = "GET" } = options;
  const fullUrl = `${import.meta.env.VITE_NANI_CLUB_API_URL}${url}`;
  const cleanPath = getGeneralizedPath(url);

  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["authorization"] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(fullUrl, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    Sentry.withScope((scope) => {
      scope.setTag("type", "network_crash");
      scope.setTag("api_source", "naniclub");
      scope.setFingerprint(["network-crash", method, cleanPath]);
      scope.setExtra("full_url", fullUrl);
      scope.setExtra("request_body", body);
      scope.setExtra("method", method);
      Sentry.captureException(e);
    });
    sentryMarkAsReported(e);
    throw e;
  }

  if (response.status === 401) {
    handleUnauthorized();
    throw new UnauthorizedError();
  }

  if (!response.ok) {
    const error = new Error("ERROR");
    Sentry.withScope((scope) => {
      scope.setTag("api_path", cleanPath);
      scope.setTag("api_method", method);
      scope.setTag("api_source", "naniclub");
      scope.setTag("http_status", response.status);
      scope.setFingerprint([method, cleanPath]);
      scope.setExtra("full_url", fullUrl);
      scope.setExtra("request_body", body);
      scope.setLevel("warning");
      Sentry.captureMessage(
        `API Error: ${method} ${cleanPath} [${response.status}]`
      );
    });
    sentryMarkAsReported(error);
    throw error;
  }

  const result = (await response.json()) as NaniClubResponse<T>;

  if (result.err !== 0) {
    Sentry.withScope((scope) => {
      scope.setTag("api_code", String(result.err));
      scope.setTag("api_path", cleanPath);
      scope.setTag("api_method", method);
      scope.setTag("api_source", "naniclub");
      scope.setFingerprint([method, cleanPath]);
      scope.setExtra("full_url", fullUrl);
      scope.setExtra("request_body", body);
      scope.setExtra("error", result);
      scope.setLevel("warning");
      Sentry.captureMessage(`API Error: ${method} ${cleanPath}`);
    });
    const failureError = new Error(result.msg);
    sentryMarkAsReported(failureError);
    throw failureError;
  }

  return result.data;
};
